import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';

import type { InitialMessage } from '@databox/types/domains/ai/chat';
import { ChatMessageType, type ChatMode } from '@databox/types/domains/ai/chat.enums';
import type { ContextObjectBase } from '@databox/types/domains/ai/context';

export interface AiChatSetupOptions {
	contextItems?: ContextObjectBase[];
	initialMessages?: InitialMessage[];
	initialPrompt?: string;
	// The thread Genie starts from the onboarding answers; leaving it is confirmed once per user.
	isOnboardingChat?: boolean;
	// The clients picked where the conversation started (Home's composer); the chat's selector seeds from them.
	selectedAccountIds?: number[];
	// The mode picked where the conversation started (Home's composer); the chat starts in it.
	chatMode?: ChatMode;
}

export default abstract class GenieThreadBaseController extends Controller {
	override queryParams = ['chat'];

	@tracked chat: string | null = null;
	@tracked contextItems: ContextObjectBase[] = [];
	@tracked initialMessages: InitialMessage[] = [];
	// The controlled thread id reflected in the URL; passed to the React mount as `@threadId`.
	@tracked threadId: string | null = null;
	// The controlled artifact id reflected in the URL; passed to the React mount as `@artifactId`.
	@tracked artifactId: string | null = null;
	// Seeds the welcome composer without sending; passed to the React mount as `@initialPrompt`.
	@tracked initialPrompt: string | null = null;
	isOnboardingChat = false;
	@tracked selectedAccountIds: number[] = [];
	@tracked chatMode: ChatMode | null = null;

	setup(options: AiChatSetupOptions): void {
		this.contextItems = options.contextItems ?? [];
		this.initialPrompt = options.initialPrompt ?? null;
		this.isOnboardingChat = options.isOnboardingChat ?? false;
		this.selectedAccountIds = options.selectedAccountIds ?? [];
		this.chatMode = options.chatMode ?? null;

		const messages = options.initialMessages ?? [];
		const ai = messages.filter((m) => m.role === ChatMessageType.AI);
		const human = messages.filter((m) => m.role === ChatMessageType.HUMAN);
		this.initialMessages = [...ai, ...human];
	}

	// Controllers are singletons, so every per-visit field must be cleared on exit — a field left out
	// here leaks into the next visit. Routes call this from `resetController` instead of clearing
	// fields one by one.
	reset(): void {
		this.chat = null;
		this.contextItems = [];
		this.initialMessages = [];
		this.threadId = null;
		this.artifactId = null;
		this.initialPrompt = null;
		this.isOnboardingChat = false;
		this.selectedAccountIds = [];
		this.chatMode = null;
	}
}
